import client from './client'
import type {
  StoredBackup,
  Site, FileEntry, CommandResult, LoginResponse, User,
  CertInfo, LogLines, VHostParams, UpstreamParams, IssueCertRequest,
  SiteLogInfo, SearchResult, MigrationSite, UserDetail,
  NotifyConfig, NotificationLog, ConfigSearchHit,
  ModSecStatus, ModSecConfig, ModSecRuleFile, ModSecRuleSummary,
  ModSecSiteStatus, ModSecAuditEntry,
  SnapshotMeta, Snapshot, HistoryKind,
  AuditEntry, CertExpiryInfo, NginxMetrics, GlobalSettings,
  HealthResult, HealthSummary,
  DailyStat, HourlyStat, TopEntry, TopKind,
  RateLimitConfig, ACLEntry,
  StackTemplate,
  Suggestion, AnalyzerStatus, ApplyResult,
  SchedulerJob, SchedulerRun,
  TOTPSetup,
  SecurityHeadersOptions,
} from '../types'

// wsUrlFor builds the WebSocket URL for a backend path. It honours the
// axios client's baseURL (dev uses '/api/v1' via Vite proxy; prod can
// use an absolute URL). For absolute bases we rewrite http→ws; for
// relative bases we use the page origin.
function wsUrlFor(apiPath: string): string {
  const base = (client.defaults.baseURL || '/api/v1').replace(/\/+$/, '')
  const suffix = apiPath.startsWith('/') ? apiPath : '/' + apiPath
  if (/^https?:\/\//i.test(base)) {
    return base.replace(/^http/, 'ws') + suffix
  }
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${proto}//${window.location.host}${base}${suffix}`
}

export const authApi = {
  setupStatus: () => client.get<{ setup_complete: boolean }>('/auth/setup'),
  setup: (username: string, password: string) =>
    client.post<{ user: User }>('/auth/setup', { username, password }),
  // login may return either a full LoginResponse or {requires_totp, partial_token}
  login: (username: string, password: string) =>
    client.post<LoginResponse | { requires_totp: true; partial_token: string }>(
      '/auth/login', { username, password }),
  totpVerify: (partial_token: string, code: string) =>
    client.post<LoginResponse>('/auth/totp-verify', { partial_token, code }),
  me: () => client.get<User>('/auth/me'),
  changePassword: (old_password: string, new_password: string) =>
    client.post<{ ok: boolean }>('/auth/change-password', { old_password, new_password }),
  completeOnboarding: () =>
    client.post<{ ok: boolean }>('/auth/onboarding/complete'),

  totpSetup:   () => client.get<TOTPSetup>('/auth/totp/setup'),
  totpEnable:  (secret: string, code: string) =>
    client.post<{ ok: boolean }>('/auth/totp/enable', { secret, code }),
  totpDisable: (password: string) =>
    client.post<{ ok: boolean }>('/auth/totp/disable', { password }),

  forgotPassword: (username: string) =>
    client.post<{ ok: boolean }>('/auth/forgot-password', { username }),
  resetPassword: (token: string, new_password: string) =>
    client.post<{ ok: boolean }>('/auth/reset-password', { token, new_password }),
}

export const nginxApi = {
  status:  () => client.get<CommandResult>('/nginx/status'),
  test:    () => client.post<CommandResult>('/nginx/test'),
  reload:  () => client.post<CommandResult>('/nginx/reload'),
  restart: () => client.post<CommandResult>('/nginx/restart'),

  listFiles: (path = '') =>
    client.get<{ root: string; path: string; entries: FileEntry[] }>('/nginx/files', { params: { path } }),
  readFile: (path: string) =>
    client.get<{ path: string; content: string }>('/nginx/file', { params: { path } }),
  writeFile: (path: string, content: string, validate = true) =>
    client.put<{ ok: boolean }>('/nginx/file', { path, content, validate }),
}

export const sitesApi = {
  list: () => client.get<{ sites: Site[] }>('/sites'),
  read: (name: string) => client.get<{ name: string; content: string }>(`/sites/${encodeURIComponent(name)}`),
  write: (name: string, content: string, validate = true) =>
    client.put<{ ok: boolean }>(`/sites/${encodeURIComponent(name)}`, { content, validate }),
  remove: (name: string) => client.delete<{ ok: boolean }>(`/sites/${encodeURIComponent(name)}`),
  enable: (name: string) => client.post<{ ok: boolean }>(`/sites/${encodeURIComponent(name)}/enable`),
  disable: (name: string) => client.post<{ ok: boolean }>(`/sites/${encodeURIComponent(name)}/disable`),
  bulkEnable:  (names: string[]) => client.post<{ ok: boolean; failed: string[] }>('/sites/bulk/enable',  { names }),
  bulkDisable: (names: string[]) => client.post<{ ok: boolean; failed: string[] }>('/sites/bulk/disable', { names }),
  bulkDelete:  (names: string[]) => client.delete<{ ok: boolean; failed: string[] }>('/sites/bulk', { data: { names } }),
}

export const searchApi = {
  search: (q: string, limit = 100) =>
    client.get<{ results: ConfigSearchHit[]; count: number }>('/search', { params: { q, limit } }),
}

export const templatesApi = {
  vhost:    (p: VHostParams)    => client.post<{ content: string }>('/templates/vhost', p),
  upstream: (p: UpstreamParams) => client.post<{ content: string }>('/templates/upstream', p),
  list:     () => client.get<{ templates: StackTemplate[] }>('/templates'),
  stack:    (id: string, params: Record<string, string>) =>
    client.post<{ content: string }>('/templates/stack', { id, params }),
  securityHeaders: (opts: SecurityHeadersOptions) =>
    client.post<{ content: string }>('/templates/security-headers', opts),
}

export const backupApi = {
  // Backup streams a zip; the browser handles the download via the raw URL.
  downloadUrl: () => '/api/v1/nginx/backup',
  restore: (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return client.post<{ ok: boolean; filename: string; size: number }>(
      '/nginx/restore', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  // Backups the scheduled job already wrote to the server, so a nightly zip
  // can be restored without a download/upload round trip.
  list: () => client.get<{ backups: StoredBackup[]; dir: string }>('/nginx/backups'),
  restoreStored: (name: string) =>
    client.post<{ ok: boolean; filename: string }>(
      `/nginx/backups/${encodeURIComponent(name)}/restore`),
  remove: (name: string) =>
    client.delete<{ ok: boolean }>(`/nginx/backups/${encodeURIComponent(name)}`),
}

export const healthApi = {
  history:  (name: string, limit = 50) =>
    client.get<{ results: HealthResult[] }>(`/sites/${encodeURIComponent(name)}/health`, { params: { limit } }),
  checkNow: (name: string) =>
    client.post<{ result: HealthResult; probe_url: string }>(`/sites/${encodeURIComponent(name)}/health/check`),
  summary:  () =>
    client.get<{ summaries: HealthSummary[]; down: number }>('/health/summary'),
}

export const analyticsApi = {
  summary: (name: string, from?: string, to?: string) =>
    client.get<{ days: DailyStat[]; from: string; to: string }>(
      `/sites/${encodeURIComponent(name)}/analytics/summary`, { params: { from, to } }),
  hourly:  (name: string, date?: string) =>
    client.get<{ hours: HourlyStat[]; date: string }>(
      `/sites/${encodeURIComponent(name)}/analytics/hourly`, { params: { date } }),
  // `to` is optional: without it the response covers the single day in `date`.
  top:     (name: string, kind: TopKind, date?: string, limit = 10, to?: string) =>
    client.get<{ entries: TopEntry[]; kind: TopKind; date: string; to: string }>(
      `/sites/${encodeURIComponent(name)}/analytics/top`, { params: { kind, date, limit, to } }),
  aggregate: (name: string, date?: string) =>
    client.post<{ ok: boolean; date: string }>(
      `/sites/${encodeURIComponent(name)}/analytics/aggregate`, null, { params: { date } }),
}

export const securityApi = {
  getRateLimit:    (name: string) => client.get<RateLimitConfig>(`/sites/${encodeURIComponent(name)}/ratelimit`),
  setRateLimit:    (name: string, cfg: RateLimitConfig) =>
    client.put<{ ok: boolean }>(`/sites/${encodeURIComponent(name)}/ratelimit`, cfg),
  removeRateLimit: (name: string) =>
    client.delete<{ ok: boolean }>(`/sites/${encodeURIComponent(name)}/ratelimit`),
  getACL:          (name: string) => client.get<{ entries: ACLEntry[] }>(`/sites/${encodeURIComponent(name)}/acl`),
  setACL:          (name: string, entries: ACLEntry[]) =>
    client.put<{ ok: boolean }>(`/sites/${encodeURIComponent(name)}/acl`, { entries }),
}

export const analyzerApi = {
  status: () => client.get<AnalyzerStatus>('/analyzer/status'),
  scanSite: (name: string, opts?: { ai?: boolean; lines?: number }) =>
    client.get<{ suggestions: Suggestion[] }>(
      `/sites/${encodeURIComponent(name)}/analyze`,
      { params: { ai: opts?.ai ? 'true' : undefined, lines: opts?.lines } }),
  scanAll: (opts?: { ai?: boolean; lines?: number }) =>
    client.get<{ results: Record<string, Suggestion[]> }>(
      '/analyzer/scan',
      { params: { ai: opts?.ai ? 'true' : undefined, lines: opts?.lines } }),
  apply: (name: string, suggestion: Suggestion, preview = false) =>
    client.post<ApplyResult>(
      `/sites/${encodeURIComponent(name)}/analyze/apply`,
      { suggestion, preview }),
}

export const logsApi = {
  tail: (kind: 'access' | 'error', lines = 200) =>
    client.get<LogLines>(`/logs/${kind}`, { params: { lines } }),
  // WS URL for the global log stream. The caller appends ?token=<jwt>.
  streamUrl: (kind: 'access' | 'error') => wsUrlFor(`/logs/${kind}/stream`),
}

export const siteLogsApi = {
  info: (name: string) =>
    client.get<SiteLogInfo>(`/sites/${encodeURIComponent(name)}/logs/info`),
  tail: (name: string, kind: 'access' | 'error', lines = 200) =>
    client.get<LogLines>(`/sites/${encodeURIComponent(name)}/logs/${kind}`, { params: { lines } }),
  streamUrl: (name: string, kind: 'access' | 'error') =>
    wsUrlFor(`/sites/${encodeURIComponent(name)}/logs/${kind}/stream`),
  search: (name: string, kind: 'access' | 'error', q: string, limit = 500) =>
    client.get<{ results: SearchResult[] }>(`/sites/${encodeURIComponent(name)}/logs/${kind}/search`, { params: { q, limit } }),
  downloadUrl: (name: string, kind: 'access' | 'error') =>
    `/api/v1/sites/${encodeURIComponent(name)}/logs/${kind}/download`,
  migratePreview: () =>
    client.get<{ sites: MigrationSite[] }>('/admin/logs/migrate/preview'),
  migrate: (sites: string[]) =>
    client.post<{ migrated: string[]; failed: string[] }>('/admin/logs/migrate', { sites }),
  writeLogrotate: (path?: string) =>
    client.post<{ path: string; content: string }>('/admin/logs/logrotate', { path }),
}

export const usersApi = {
  list: () => client.get<{ users: UserDetail[] }>('/admin/users'),
  get: (id: number) => client.get<UserDetail>(`/admin/users/${id}`),
  create: (data: { username: string; password: string; role: string; sites_quota: number }) =>
    client.post<UserDetail>('/admin/users', data),
  update: (id: number, data: { role: string; sites_quota: number }) =>
    client.put<{ ok: boolean }>(`/admin/users/${id}`, data),
  resetPassword: (id: number, password: string) =>
    client.post<{ ok: boolean }>(`/admin/users/${id}/reset-password`, { password }),
  remove: (id: number) => client.delete<{ ok: boolean }>(`/admin/users/${id}`),
  getSites: (id: number) => client.get<{ sites: string[] }>(`/admin/users/${id}/sites`),
  setSites: (id: number, sites: string[]) =>
    client.put<{ ok: boolean }>(`/admin/users/${id}/sites`, { sites }),
  bulkAssignSites: (id: number, sites: string[]) =>
    client.post<{ ok: boolean }>(`/admin/users/${id}/sites/bulk`, { sites }),
}

export const certsApi = {
  status: () => client.get<{ enabled: boolean; installed: boolean }>('/certs/status'),
  list:   () => client.get<{ certificates: CertInfo[] }>('/certs'),
  issue:  (req: IssueCertRequest) => client.post<CommandResult>('/certs/issue', req),
  renew:  () => client.post<CommandResult>('/certs/renew'),
  expiry: () => client.get<{ certificates: CertExpiryInfo[] }>('/certs/expiry'),
}

export const historyApi = {
  list:    (path: string, kind: HistoryKind = 'site') =>
    client.get<{ snapshots: SnapshotMeta[] }>('/history', { params: { path, kind } }),
  get:     (id: number) => client.get<Snapshot>(`/history/${id}`),
  restore: (id: number) => client.post<{ ok: boolean }>(`/history/${id}/restore`),
  diff:    (from_id: number, to_id: number) =>
    client.get<{ from: Snapshot; to: Snapshot }>('/history/diff', { params: { from_id, to_id } }),
}

export const auditApi = {
  list: (params?: { page?: number; limit?: number; username?: string; action?: string; from?: string; to?: string }) =>
    client.get<{ entries: AuditEntry[]; total: number; page: number; limit: number }>('/audit', { params }),
  actions: () => client.get<{ actions: string[] }>('/audit/actions'),
}

export const metricsApi = {
  nginx: () => client.get<NginxMetrics>('/nginx/metrics'),
}

export const globalSettingsApi = {
  get: () => client.get<GlobalSettings>('/nginx/global'),
  set: (g: GlobalSettings) => client.put<{ ok: boolean }>('/nginx/global', g),
}

export const modsecApi = {
  status:    () => client.get<ModSecStatus>('/modsec/status'),
  getConfig: () => client.get<ModSecConfig>('/modsec/config'),
  setConfig: (cfg: ModSecConfig) => client.put<{ ok: boolean }>('/modsec/config', cfg),

  listRules: () => client.get<{ files: ModSecRuleFile[] }>('/modsec/rules'),
  readRule:  (file: string) =>
    client.get<{ name: string; content: string; rules: ModSecRuleSummary[] }>(
      `/modsec/rules/${encodeURIComponent(file)}`),
  writeRule: (file: string, content: string) =>
    client.put<{ ok: boolean }>(`/modsec/rules/${encodeURIComponent(file)}`, { content }),
  enableRule:  (file: string) => client.post<{ ok: boolean }>(`/modsec/rules/${encodeURIComponent(file)}/enable`),
  disableRule: (file: string) => client.post<{ ok: boolean }>(`/modsec/rules/${encodeURIComponent(file)}/disable`),
  addExclusion: (directive: string) => client.post<{ ok: boolean }>('/modsec/exclusions', { directive }),

  siteStatus:  (name: string) => client.get<ModSecSiteStatus>(`/modsec/sites/${encodeURIComponent(name)}`),
  siteEnable:  (name: string, rulesFile?: string) =>
    client.post<{ ok: boolean; rules_file: string }>(`/modsec/sites/${encodeURIComponent(name)}/enable`,
      rulesFile ? { rules_file: rulesFile } : {}),
  siteDisable: (name: string) => client.post<{ ok: boolean }>(`/modsec/sites/${encodeURIComponent(name)}/disable`),
  readSiteRules: (name: string) =>
    client.get<{ content: string; path: string }>(`/modsec/sites/${encodeURIComponent(name)}/rules`),
  writeSiteRules: (name: string, content: string) =>
    client.put<{ ok: boolean }>(`/modsec/sites/${encodeURIComponent(name)}/rules`, { content }),

  auditLog: (params: { lines?: number; ip?: string; rule_id?: string; site?: string }) =>
    client.get<{ entries: ModSecAuditEntry[] }>('/modsec/auditlog', { params }),
}

export const schedulerApi = {
  listJobs: () => client.get<{ jobs: SchedulerJob[] }>('/scheduler/jobs'),
  runNow:   (job: string) =>
    client.post<{ run: SchedulerRun }>(`/scheduler/jobs/${encodeURIComponent(job)}/run`),
  history:  (job: string, limit = 20) =>
    client.get<{ runs: SchedulerRun[] }>(
      `/scheduler/jobs/${encodeURIComponent(job)}/runs`,
      { params: { limit } }),
}

export const notificationsApi = {
  getConfig: () => client.get<NotifyConfig>('/notifications/config'),
  setConfig: (cfg: NotifyConfig) => client.put<{ ok: boolean }>('/notifications/config', cfg),
  test: () => client.post<{ ok: boolean; error?: string }>('/notifications/test'),
  listLog: (params?: { page?: number; limit?: number; status?: string }) =>
    client.get<{ logs: NotificationLog[]; total: number; page: number; limit: number }>(
      '/notifications/log', { params }
    ),
}
